import { DirectiveBinding, ObjectDirective } from "vue";

let tooltipElement: HTMLElement | null = null;

const createTooltipElement = () => {
  if (!tooltipElement) {
    tooltipElement = document.createElement("div");
    tooltipElement.className = "app-custom-tooltip";
    document.body.appendChild(tooltipElement);
  }
  return tooltipElement;
};

const showTooltip = (event: MouseEvent, text: string) => {
  const el = createTooltipElement();
  el.textContent = text;
  el.classList.add("visible");
  updateTooltipPosition(event);
};

const hideTooltip = () => {
  if (tooltipElement) {
    tooltipElement.classList.remove("visible");
  }
};

const updateTooltipPosition = (event: MouseEvent) => {
  if (!tooltipElement) return;
  // Offset a bit so cursor doesn't cover the tooltip
  const offset = 15;
  let x = event.clientX + offset;
  let y = event.clientY + offset;

  // Prevent overflowing right edge
  const tooltipRect = tooltipElement.getBoundingClientRect();
  if (x + tooltipRect.width > window.innerWidth) {
    x = event.clientX - tooltipRect.width - offset;
  }

  // Prevent overflowing bottom edge
  if (y + tooltipRect.height > window.innerHeight) {
    y = event.clientY - tooltipRect.height - offset;
  }

  tooltipElement.style.left = `${x}px`;
  tooltipElement.style.top = `${y}px`;
};

interface TooltipElement extends HTMLElement {
  _tooltipText?: string;
  _mouseEnterHandler?: (e: MouseEvent) => void;
  _mouseLeaveHandler?: () => void;
  _mouseMoveHandler?: (e: MouseEvent) => void;
}

export const tooltipDirective: ObjectDirective<TooltipElement> = {
  mounted(el, binding: DirectiveBinding) {
    el._tooltipText = binding.value;

    // Add custom cursor class if not disabled
    if (
      !el.hasAttribute("disabled") &&
      !el.classList.contains("cursor-not-allowed")
    ) {
      el.classList.add("has-tooltip");
    }

    el._mouseEnterHandler = (e: MouseEvent) => {
      if (el._tooltipText) {
        showTooltip(e, el._tooltipText);
      }
    };

    el._mouseMoveHandler = (e: MouseEvent) => {
      if (el._tooltipText) {
        updateTooltipPosition(e);
      }
    };

    el._mouseLeaveHandler = () => {
      hideTooltip();
    };

    el.addEventListener("mouseenter", el._mouseEnterHandler);
    el.addEventListener("mousemove", el._mouseMoveHandler);
    el.addEventListener("mouseleave", el._mouseLeaveHandler);
  },
  updated(el, binding: DirectiveBinding) {
    el._tooltipText = binding.value;

    // Update cursor logic dynamically just in case disabled state changed
    if (
      el.hasAttribute("disabled") ||
      el.classList.contains("cursor-not-allowed")
    ) {
      el.classList.remove("has-tooltip");
    } else if (el._tooltipText) {
      el.classList.add("has-tooltip");
    }

    // If tooltip text becomes empty while visible, hide it
    if (!binding.value && tooltipElement?.classList.contains("visible")) {
      hideTooltip();
    }
  },
  unmounted(el) {
    if (el._mouseEnterHandler)
      el.removeEventListener("mouseenter", el._mouseEnterHandler);
    if (el._mouseMoveHandler)
      el.removeEventListener("mousemove", el._mouseMoveHandler);
    if (el._mouseLeaveHandler)
      el.removeEventListener("mouseleave", el._mouseLeaveHandler);
    hideTooltip();
  },
};
